const ACCESS_KEY = 'mir-ai.auth.accessToken';
const REFRESH_KEY = 'mir-ai.auth.refreshToken';
const PERSIST_KEY = 'mir-ai.auth.persist';

type Kind = 'local' | 'session';

function pick(kind: Kind): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readFromEither(key: string): { value: string; kind: Kind } | null {
  const local = pick('local');
  const fromLocal = local?.getItem(key) ?? null;
  if (fromLocal !== null) return { value: fromLocal, kind: 'local' };

  const session = pick('session');
  const fromSession = session?.getItem(key) ?? null;
  if (fromSession !== null) return { value: fromSession, kind: 'session' };

  return null;
}

function writeTo(kind: Kind, key: string, value: string): void {
  const storage = pick(kind);
  try {
    storage?.setItem(key, value);
  } catch {
    /* quota/exceptions are non-fatal */
  }
}

function removeAll(key: string): void {
  try {
    pick('local')?.removeItem(key);
  } catch {
    /* ignore */
  }
  try {
    pick('session')?.removeItem(key);
  } catch {
    /* ignore */
  }
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface SetTokensOptions {
  /** Persist to `localStorage` (survives browser close) or `sessionStorage` (tab-scoped). Default: true. */
  remember?: boolean;
}

/**
 * Persisted token store. Writes to `localStorage` by default ("remember me")
 * or `sessionStorage` when `remember: false` is passed. Always reads from
 * whichever storage has a value — useful for graceful migration on toggle.
 */
export const tokenStorage = {
  getAccessToken(): string | null {
    return readFromEither(ACCESS_KEY)?.value ?? null;
  },
  getRefreshToken(): string | null {
    return readFromEither(REFRESH_KEY)?.value ?? null;
  },
  /**
   * Was the *current* session stored with "remember me" enabled? Returns
   * `null` if there is no active session.
   */
  isPersistent(): boolean | null {
    const access = readFromEither(ACCESS_KEY);
    if (!access) return null;
    return access.kind === 'local';
  },
  setTokens({ accessToken, refreshToken }: TokenPair, options: SetTokensOptions = {}): void {
    const remember = options.remember ?? true;
    const kind: Kind = remember ? 'local' : 'session';
    // Clear the *other* bucket first so we never leak tokens across kinds.
    removeAll(ACCESS_KEY);
    removeAll(REFRESH_KEY);
    writeTo(kind, ACCESS_KEY, accessToken);
    writeTo(kind, REFRESH_KEY, refreshToken);
    writeTo('local', PERSIST_KEY, remember ? '1' : '0');
  },
  clear(): void {
    removeAll(ACCESS_KEY);
    removeAll(REFRESH_KEY);
    removeAll(PERSIST_KEY);
  },
};
